import * as React from 'react';
import type { FC, RefObject, SyntheticEvent } from 'react';

import { batch } from '@legendapp/state';
import { use$ } from '@legendapp/state/react';
import { styled } from '@volkswagen-onehub/components-core';

import translate from '../../utils/translate';
import { getElementByTestId } from '../option-selector/utils';

import { ContentId, TrackingEventName } from '../../store/stores/tracking/types';

import {
	disclaimerStore,
	emissionStoreActions,
	fullscreenStore,
	interiorStore,
	interiorStoreActions,
	rootStore,
	rootStoreActions,
	zoomStore
} from '../../store';
import { trackingStore } from '../../store/stores/tracking-store';
import { REFERENCE_BADGE_ID } from '../header/reference-badge';
import { MiddleTruncateSelect } from './middle-truncate-select';

interface TrimlineDropdownComponentProps {
	trimlineSelectionDropdownRef?: RefObject<HTMLDivElement>;
	testId: string;
	getTruncatedLabel: (val?: string) => string;
	dumbVariantNameSpanRef: RefObject<HTMLSpanElement>;
	dumbLabelSpanRef: RefObject<HTMLSpanElement>;
	charsToTruncate: number;
}

const TrimlineDropdownComponent: FC<TrimlineDropdownComponentProps> = ({
	trimlineSelectionDropdownRef,
	testId,
	getTruncatedLabel,
	dumbVariantNameSpanRef,
	dumbLabelSpanRef,
	charsToTruncate
}) => {
	const isZoomed = use$(zoomStore.isZoomed);
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const activeDisclaimer = use$(disclaimerStore.activeDisclaimer);
	const bffData = use$(rootStore.bffData);
	const [currentModelKey, currentModelValue] = use$(rootStore.currentModel);
	const { familyName, trimName } = currentModelValue.vehicleConfiguration || {};
	const track = trackingStore.track;

	const isDisabledSelect = Object.keys(bffData?.assets || {}).length <= 1;
	const disabledSelect = isFullscreenMode || isZoomed || isDisabledSelect;

	const handleTrimlineChange = (evt: SyntheticEvent<HTMLSelectElement>) => {
		const { value } = evt.currentTarget;
		batch(() => {
			const newModel = bffData?.assets?.[value];
			if (!newModel) return;

			const [previousVariantKey] = rootStore.currentVariant.get();
			const [previousColorKey, previousRimKey] = previousVariantKey.split('-');
			const [previousInteriorKey] = interiorStore.currentInterior.get();

			rootStoreActions.setModelData([value, newModel]);

			if (newModel?.exterior && Object.entries(newModel.exterior).length > 0) {
				const exteriorEntries = Object.entries(newModel.exterior);
				const [variantKey, variantValue] =
					exteriorEntries.find(([key]) => key === previousVariantKey) ??
					exteriorEntries.find(([key]) => key.split('-')[0] === previousColorKey) ??
					exteriorEntries.find(([key]) => key.split('-')[1] === previousRimKey) ??
					exteriorEntries[0]!;
				if (variantKey && variantValue) {
					rootStoreActions.setVariantData([variantKey, variantValue?.assets, variantValue?.baseUrl]);
				}
			}
			if (newModel?.interior && Object.entries(newModel.interior).length > 0) {
				const preservedInterior =
					previousInteriorKey && newModel.interior[previousInteriorKey]
						? ([previousInteriorKey, newModel.interior[previousInteriorKey]] as const)
						: Object.entries(newModel.interior)[0]!;
				const [interiorKey, interiorValue] = preservedInterior;
				if (interiorKey && interiorValue) {
					interiorStoreActions.setInteriorData([interiorKey, interiorValue?.assets]);
				}
			}
			if (newModel?.trimLayerDetails) {
				emissionStoreActions.setEmissionData(newModel.trimLayerDetails);
			}
		});

		if (!activeDisclaimer) {
			setTimeout(() => {
				const referenceBadge = getElementByTestId(REFERENCE_BADGE_ID) as HTMLButtonElement;
				if (referenceBadge) referenceBadge.focus();
			}, 500);
		}

		track(TrackingEventName.VISUALIZER_TRIM_CLICK, { contentId: ContentId.MENU });
	};

	return (
		<StyledDropdownWrapper ref={trimlineSelectionDropdownRef} data-testid={testId}>
			<DumbestLabelSpan ref={dumbLabelSpanRef}>{familyName}</DumbestLabelSpan>
			<DumbestVariantSpan ref={dumbVariantNameSpanRef}>{trimName}</DumbestVariantSpan>
			<StyledSelect
				aria-label={translate('global.ariaLabel.trimlineDropdown')}
				label={getTruncatedLabel(familyName)}
				value={currentModelKey}
				name="trimlineSelection"
				disabled={disabledSelect}
				$isDissabled={disabledSelect}
				onChange={handleTrimlineChange}
				charsToTruncate={charsToTruncate}
			>
				{Object.entries(bffData?.assets || {}).map(([modelKey, trimlineValue]) => {
					const fullTrimName = trimlineValue.vehicleConfiguration.trimName || '$$MISSING_TRANSLATION_KEY$$';

					const isActive = trimName === fullTrimName;

					const ariaText = isActive
						? `${translate('global.ariaLabel.activeTrimline')}: ${fullTrimName}`
						: `${translate('global.ariaLabel.inactiveTrimline')}: ${fullTrimName}`;

					return (
						<option
							key={modelKey}
							value={modelKey}
							aria-label={ariaText}
							aria-selected={isActive}
							title={fullTrimName}
							data-testid={`${testId}_Option_${modelKey}`}
						>
							{fullTrimName}
						</option>
					);
				})}
			</StyledSelect>
		</StyledDropdownWrapper>
	);
};

const StyledSelect = styled(MiddleTruncateSelect)<{ $isDissabled: boolean }>`
	color: ${({ $isDissabled, theme }) => ($isDissabled ? theme.colors.content.primary : 'inherit')};
	& * {
		cursor: ${({ $isDissabled }) => ($isDissabled ? 'default' : 'pointer')};
	}
	& [class*='StyledIconWrapper'] {
		left: ${({ theme }) => (theme.direction === 'ltr' ? '0' : theme.size.static150)};
		display: ${({ $isDissabled }) => ($isDissabled ? 'none' : 'flex')};
	}

	${({ theme }) =>
		theme.variant === 'v26' &&
		`
			&& [class*='StyledLabel'] {
				color: #ffffff;
			}

			&& [class*='StyledLabelText'] {
				color: #ffffff;
			}

			&& [class*='StyledSelect'] {
				color: #ffffff;
			}

			&& [class*='StyledLabel'] {
				color: #ffffff;
			}

			&& [class*='StyledLabel']:hover [class*='StyledLabelText'] {
				color: #ffffff;
			}
		`}
`;

const BaseHiddenSpan = styled.span`
	visibility: hidden;
	position: absolute;
	white-space: pre;
`;

const DumbestVariantSpan = styled(BaseHiddenSpan)`
	font-size: ${({ theme }) => theme.fonts.copy[200].fontSize};
	font-family: ${({ theme }) => theme.fonts.copy[200].fontFamily};
`;

const DumbestLabelSpan = styled(BaseHiddenSpan)`
	font-size: ${({ theme }) => theme.fonts.label[100].fontSize};
`;

const StyledDropdownWrapper = styled.div`
	width: 90%;
	max-width: 315px;
	color: ${({ theme }) => (theme.variant === 'v26' ? '#ffffff' : 'inherit')};
	margin-bottom: ${({ theme }) => theme.variant === 'v26' && theme.size.static150};
`;

export default TrimlineDropdownComponent;
